import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import { debounce } from "../../commonFunctions/arrayFunc";
import { saveFile } from "../../commonFunctions/fileFolderConfig";
import ProductionCard from "../../components/AppSettingsCards/ProductionCard";
import StagingCard from "../../components/AppSettingsCards/StagingCard";
import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
// import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar"; // Removed Sidebar
import { useAppOrgContext } from "../../context/AppOrgContext";
import { toastSuccess } from "../../utils/toastUtils";
import { useModalContext } from "../../context/ModalContext";
import useFetchData from "../../hooks/useFetchData";
import Pagination from "../ApplicationUsers/Pagination";
import AddApplicationModal from "./AddApplicationModal";
import ImportAppModal from "./ImportAppModal";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import storageService from "../../services/storageService";
import { IS_PRODUCTION, IS_SANDBOX, STORAGE_KEYS } from "../../constant/constant";
import { getToolTipAttributes } from "../../utils/tooltip";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import "./AssignUserToApplication.css";

const MODAL_TYPES = {
  APP: "APP",
  IMPORT: "IMPORT",
  ROLLBACK: "ROLLBACK",
  CHECKPOINT: "CHECKPOINT"
};

const convertToLocalTime = (utcTimestamp) => {
  const removeCommas = (str) => str.replace(/,/g, " ");
  const date = new Date(utcTimestamp);
  const dateStrinf = date.toLocaleString();
  return removeCommas(dateStrinf);
};

const RollbackAppVersion = ({ appId, onSave, onRefresh }) => {
  const headers = useMemo(() => ({ "Zino-App-Id": appId }), [appId]);

  const { data, loading, error } = useFetchData(`/application/backups`, [appId], [], headers);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [comment, setComment] = useState("");
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  const version_options =
    data?.backups?.map((backup) => ({
      id: `${backup.id}`,
      version_number: `v.${backup.version_number}`,
      created_at: convertToLocalTime(backup.created_at),
      comment: backup.comment
    })) || [];

  const handleUpdateVersion = (obj) => {
    if (obj?.id) {
      setSelectedVersion(obj.id);
    }
  };

  const handleRollback = async () => {
    if (selectedVersion) {
      setIsRollingBack(true);
      setSuccessMessage("");
      try {
        await onSave(selectedVersion, appId, comment);

        setSuccessMessage("Rollback successful!");
        onRefresh();
      } catch (error) {
        setSuccessMessage("Rollback failed. Please try again.");
      } finally {
        setIsRollingBack(false);
      }
    }
  };

  return (
    <div className="builder_form">
      <SingleSelectBox
        selectedData={selectedVersion}
        options={[{ version_number: "Select Version" }, ...version_options]}
        labelKey={["version_number", "created_at", "comment"]}
        onChange={handleUpdateVersion}
        label="Application Version"
        placeholder="Select Version"
        isSearch={true}
      />

      <Input
        placeholder="Enter rollback comment"
        label="Comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <Button onClick={handleRollback} disabled={!selectedVersion || isRollingBack}>
        {isRollingBack ? "Rolling back, ." : "Set Version"}
      </Button>
      {successMessage && <div>{successMessage}</div>}
    </div>
  );
};

const CheckPointAppVersion = ({ onSave, onRefresh }) => {
  const [comment, setComment] = useState("");
  const [isSettingCheckpoint, setIsSettingCheckpoint] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleSetCheckpoint = async () => {
    if (!comment) return;

    setIsSettingCheckpoint(true);
    setSuccessMessage("");

    try {
      await onSave(comment);
      setSuccessMessage("Checkpoint set successfully!");
      onRefresh();
    } catch (error) {
      setSuccessMessage("Failed to set checkpoint. Please try again.");
    } finally {
      setIsSettingCheckpoint(false);
    }
  };

  return (
    <div className="builder_form">
      <Input
        placeholder="Enter rollback comment"
        label="Comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />

      <Button onClick={handleSetCheckpoint} disabled={!comment || isSettingCheckpoint}>
        {isSettingCheckpoint ? "Setting Checkpoint, please wait..." : "Set CheckPoint"}
      </Button>

      {successMessage && <div>{successMessage}</div>}
    </div>
  );
};

const AppSettingsHeader = ({ appData, actions, onDownloadRequirement, onBack }) => {
  const renderActions = (actionsList) =>
    actionsList
      .filter((action) => action.show)
      .map((action, index) => (
        <div
          key={index}
          onClick={action.onClick}
          {...getToolTipAttributes(action.title, "bottom")}
          style={{ transition: "background-color 0.2s" }}
          className={`other_actions_list_items setting_action_icon_wrapper cursor_pointer d_flex align_center justify_center ${action.icon === "delete_forever" ? "delete-app-button" : ""}`}
        >
          <MaterialIcon name={action.icon} className="action font_size_large" style={{ color: "#555" }} />
        </div>
      ));

  return (
    <div className="bg_light border_radius_medium p_4 shadow_sm mb_3">
      <div className="d_flex justify_between align_start">
        <div className="d_flex flex_column gap_2">
          <div className="d_flex align_center g_12">
            <div
              onClick={onBack}
              className="cursor_pointer d_flex align_center bg_light setting_back_button"
              {...getToolTipAttributes("Back to Apps", "bottom")}
            >
              <MaterialIcon name="arrow_back" style={{ fontSize: "18px", color: "#555" }} />
              <span className="font_weight_medium" style={{ fontSize: "0.9rem", color: "#555" }}>
                Back
              </span>
            </div>
            <div className="app-name font_size_xlarge font_weight_semibold setting_app_name_text">{appData.name}</div>
          </div>
          {appData.description && <p className="m_0 mt_2 setting_app_description">{appData.description}</p>}
        </div>
        <div className="d_flex gap_2 align_center">
          {renderActions(actions)}
          {appData.requirement_document_path && (
            <MaterialIcon
              name="file_save"
              className="edit_icon cursor_pointer setting_download_icon"
              title="Download requirement pdf"
              onClick={onDownloadRequirement}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const DeploymentsSection = ({
  deployments,
  searchQuery,
  onSearchChange,
  onOpenRollback,
  onOpenCheckpoint,
  pageTrack,
  pagination,
  onPageChange,
  onLimitChange
}) => {
  return (
    <div className="bg_light border_radius_medium p_4 shadow_sm mt_3">
      <div className="d_flex justify_between align_center mb_3">
        <div className="table-title font_size_large font_weight_semibold setting_section_title">
          Checkpoints & Deployments
        </div>
        <div className="d_flex justify_end g_12">
          <div
            className="other_actions_list_items cursor_pointer py_2 px_3 d_flex align_center gap_2 border_radius_small setting_action_button"
            onClick={onOpenCheckpoint}
          >
            <MaterialIcon name="flag" className="action" style={{ fontSize: "18px" }} />
            <span className="action_name font_weight_medium" style={{ fontSize: "0.9rem" }}>
              Set Checkpoint
            </span>
          </div>

          <div
            className="other_actions_list_items cursor_pointer py_2 px_3 d_flex align_center gap_2 border_radius_small setting_action_button"
            onClick={onOpenRollback}
          >
            <MaterialIcon name="settings_backup_restore" className="action" style={{ fontSize: "18px" }} />
            <span className="action_name font_weight_medium" style={{ fontSize: "0.9rem" }}>
              Rollback Version
            </span>
          </div>
        </div>
      </div>
      <div className="setting_table_search_container">
        <div className="w_25">
          <Input autoFocus placeholder="Search By Comment" value={searchQuery} onChange={onSearchChange} />
        </div>
      </div>
      <div>
        <table className="deployments-table setting_deployments_table">
          <thead>
            <tr className="setting_table_header_row">
              <th className="table-head setting_table_head_cell" style={{ width: "10%" }}>
                Version
              </th>
              <th className="table-head setting_table_head_cell" style={{ width: "10%" }}>
                Parent
              </th>
              <th className="table-head setting_table_head_cell" style={{ width: "15%" }}>
                Type
              </th>
              <th className="table-head setting_table_head_cell" style={{ width: "50%" }}>
                Comment
              </th>
              <th className="table-head setting_table_head_cell" style={{ width: "25%" }}>
                Date Created
              </th>
            </tr>
          </thead>
          <tbody>
            {deployments.map((deployment) => (
              <tr key={deployment.id} className="table-row border_bottom" style={{ borderColor: "#eee" }}>
                <td className="table-cell">v.{deployment.version_number}</td>
                <td className="table-cell">v.{deployment.parent}</td>
                <td className="table-cell border_bottom" style={{ padding: "4px 8px", borderColor: "#eee" }}>
                  <span
                    className={`badge setting_badge ${deployment.type === "manual" ? "setting_badge_manual" : "setting_badge_auto"}`}
                  >
                    {deployment.type || "auto"}
                  </span>
                </td>
                <td className="table-cell">{deployment.comment}</td>
                <td
                  className="table-cell"
                  style={{ color: "#666" }} // Specific overwrite for date color if different, or just use class. The class has color #333. Date had #666. I'll add text_secondary override.
                >
                  {convertToLocalTime(deployment.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="setting_pagination_container">
          <Pagination
            pageTrack={pageTrack}
            pagination={pagination}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      </div>
    </div>
  );
};

const AppSettingsPage = () => {
  const limit = storageService.get(STORAGE_KEYS.LIMIT) || 10;
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    search: "",
    page: 1,
    limit: Number(limit)
  });

  const [pageTrack, setPageTrack] = useState({
    currPage: 1,
    totalPages: 1
  });

  // const [isLoader, setIsLoader] = useState(false);
  // const [selectedUsers, setSelectedUsers] = useState([]);
  const { allApplications, setAllApplications } = useAppOrgContext();
  const [editData, setEditData] = useState({});
  const [activeModal, setActiveModal] = useState(null); // Consolidated Modal State
  const [deployments, setDeployments] = useState([]);
  const { getUserConfirmationForDelete } = useModalContext();
  const [appData, setAppData] = useState({});
  const params = useParams();
  const navigate = useNavigate();

  const controller = new AbortController();
  const intervalId = useRef(null);
  const statusesRequiringPolling = ["starting", "publishing", "stopping"];

  const fetchStatus = async (type) => {
    try {
      const endpoint = type === "stage" ? "/application/staging-infra" : "/application/infra";
      const { data } = await axiosInstance.get(endpoint, {
        signal: controller.signal,
        timeout: 50000,
        headers: {
          "Zino-App-Id": appData.id
        }
      });
      setAppData((prevStatusData) => ({
        ...prevStatusData,
        ...(type === "stage" ? { staging_status: data?.status } : { status: data?.status }),
        ...(type === "stage" ? { staging_url: data?.web_url } : { web_url: data?.web_url }),
        pwa_url: data?.pwa_url
      }));
    } catch (error) {
      if (error.name !== "AbortError") {
        handleApiError(error);
      }
    }
  };

  useEffect(() => {
    if (
      statusesRequiringPolling.includes(appData.status) ||
      statusesRequiringPolling.includes(appData.staging_status)
    ) {
      intervalId.current = setInterval(() => {
        if (statusesRequiringPolling.includes(appData.status)) {
          fetchStatus("prod");
        }
        if (statusesRequiringPolling.includes(appData.staging_status)) {
          fetchStatus("stage");
        }
      }, 5000);
    }

    return () => {
      controller.abort();
      clearInterval(intervalId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appData.status, appData.staging_status]);

  const getModalName = () => {
    switch (activeModal) {
      case MODAL_TYPES.APP:
        return editData.id !== undefined ? "Edit Application" : "Add Application";
      case MODAL_TYPES.IMPORT:
        return "Import App";
      case MODAL_TYPES.ROLLBACK:
        return "Rollback Application Version";
      case MODAL_TYPES.CHECKPOINT:
        return "CheckPoint Application Version";
      default:
        return "";
    }
  };

  const openAppEditModal = (e, data) => {
    e.stopPropagation();
    setEditData(data);
    setActiveModal(MODAL_TYPES.APP);
  };

  const addApplication = async (obj) => {
    try {
      const { data, message } = await axiosInstance.post(`/organisations/${params.orgId}/apps`, obj);
      const updatedData = {
        ...data,
        status: "stopped",
        web_url: "",
        pwa_url: "",
        name: data.name.charAt(0).toUpperCase() + data.name.slice(1)
      };

      const updatedApplications = [...allApplications, updatedData];
      setActiveModal(null);
      setAllApplications(updatedApplications);

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const editApplication = async (obj) => {
    try {
      const { message } = await axiosInstance.put(`/organisations/${params.orgId}/apps/${obj.id}`, obj);
      setActiveModal(null);
      setAllApplications(allApplications.map((app) => (app.id === obj.id ? obj : app)));
      setAppData({ ...appData, ...obj });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDownloadDump = async (appID) => {
    try {
      const response = await axiosInstance.get(`/app/dump`, {
        headers: {
          "Zino-App-Id": appID
        },
        responseType: "blob"
      });
      saveFile(response, "app-export.zip");
      toastSuccess("App exported successfully");
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDownloadConfig = async (appID) => {
    try {
      const response = await axiosInstance.get(`/app/config/export`, {
        headers: {
          "Zino-App-Id": appID
        },
        responseType: "blob"
      });
      saveFile(response, "config.zip");
    } catch (error) {
      handleApiError(error);
    }
  };

  const deleteApplication = async (id, name) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(name, "app");
      if (!userConfirmed) return;
      const { message } = await axiosInstance.delete(`/organisations/${params.orgId}/apps/${id}`);
      setActiveModal(null);
      setAllApplications(allApplications.filter((app) => app.id !== id));
      toastSuccess(message);
      navigate(`./${params.orgId}/app`);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleGeneratePdf = async (orgID, appID) => {
    try {
      const { message } = await axiosInstance.get(`/app-document/${orgID}/${appID}`, {
        headers: {
          "Zino-App-Id": appID
        }
      });
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSaveRollbackAppVersion = async (version, appID, comment) => {
    try {
      const numericVersion = version.replace("v.", "");

      const { message } = await axiosInstance.post(
        `/application/rollback/${numericVersion}`,
        { comment }, // Send comment in request body
        {
          headers: {
            "Zino-App-Id": appID
          }
        }
      );

      setActiveModal(null);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleSaveCheckPointAppVersion = async (comment) => {
    try {
      const { message } = await axiosInstance.post(
        `/application/checkpoint`,
        { comment } // Send comment in request body
      );

      setActiveModal(null);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDownloadRequirementPdf = async () => {
    try {
      const response = await axiosInstance.get(`/public/${appData.requirement_document_path}`, {
        responseType: "blob"
      });
      const fileName = `${appData.name}.pdf`;
      saveFile(response, fileName);
    } catch (error) {
      handleApiError(error);
    }
  };
  // need to implement willl do this later
  // const openApp = async (app, statusData) => {
  //   setApplication(app);
  //   const { staging_url, staging_status, web_url, status } = statusData;

  //   setApplicationUrl({ staging_url, staging_status, web_url, status });

  //   setApplicationId(app.id);
  //   setScreensExplorerData(false);
  //   setAppOrgnames((pre) => {
  //     return { ...pre, appName: app.name };
  //   });
  //   navigate(`./${app.id}/screen`);
  // };

  const otherActions = [
    {
      title: "Edit",
      icon: "edit",
      onClick: (e) =>
        openAppEditModal(e, {
          description: appData.description,
          name: appData.name,
          id: appData.id,
          logo: appData.logo
        }),
      show: true
    },
    {
      title: "Export Dump",
      icon: "file_download",
      onClick: () => handleDownloadDump(appData.id),
      show: true
    },
    {
      title: "Import Config",
      icon: "upload_file",
      onClick: () => setActiveModal(MODAL_TYPES.IMPORT),
      show: true
    },
    {
      title: "Download JSON",
      icon: "data_object",
      onClick: () => handleDownloadConfig(appData.id),
      show: true
    },
    {
      title: "Generate Requirements PDF",
      icon: "description",
      onClick: () => handleGeneratePdf(params.orgId, appData.id),
      show: true
    },
    {
      title: "Delete Application",
      icon: "delete_forever",
      onClick: () => deleteApplication(appData.id, appData.name),
      show: true
    }
  ];

  const debouncedFetch = useMemo(
    () =>
      debounce((query) => {
        setPagination((prev) => {
          const updatedPagination = { ...prev, search: query, page: 1 };
          fetchAppBackup(query, 1, updatedPagination.limit, params.appId);
          return updatedPagination;
        });
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const debouncedSearch = useCallback(
    (query) => {
      debouncedFetch(query);
    },
    [debouncedFetch]
  );

  const onPageChangeAPI = async (pageNumber) => {
    try {
      const { limit } = pagination;
      await fetchAppBackup(pagination.search, pageNumber, limit, params.appId);
      setPagination((prev) => ({ ...prev, page: pageNumber }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch users";
      console.error(errorMessage);
    }
  };

  const onLimitChangeAPI = async (newLimit) => {
    try {
      storageService.set(STORAGE_KEYS.LIMIT, newLimit);

      const { page } = pagination;
      await fetchAppBackup(pagination.search, page, newLimit, params.appId);
      setPagination((prev) => ({ ...prev, limit: newLimit }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch users";
      console.error(errorMessage);
    }
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  const fetchAppBackup = async (search, page, limit, appId) => {
    try {
      const response = await axiosInstance.get(`/application/backups`, {
        headers: {
          "Zino-App-Id": appId
        },
        params: {
          search,
          page,
          limit
        }
      });

      setPageTrack({
        currPage: response.data.pagination.current_page,
        totalPages: response.data.pagination.total_pages
      });

      setDeployments(response.data.backups || []);
    } catch (error) {
      console.error("Error fetching app details:", error);
      handleApiError(error);
    }
  };

  const fetchAppData = async () => {
    try {
      const { data } = await axiosInstance.get(`/organisations/${params.orgId}/app/${params.appId}`);
      setAppData(data);
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    if (params.appId && params.orgId) {
      fetchAppData();
    }
    fetchAppBackup("", 1, 10, params.appId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.appId, params.orgId]);

  return (
    <div className="application_container setting_page_container">
      {/* <AppLeftSideBar type="appSettings" /> */}
      <div className={"d_flex justify_center w_100"}>
        <div className="pt_4 px_4 setting_content_wrapper">
          <AppSettingsHeader
            appData={appData}
            actions={otherActions}
            onDownloadRequirement={handleDownloadRequirementPdf}
            onBack={() => navigate(`/organization/${params.orgId}/app`)}
          />

          <div className="setting_cards_wrapper">
            {IS_SANDBOX && <StagingCard appData={appData} title="Sandbox" setAppData={setAppData} />}
            {IS_PRODUCTION && <ProductionCard appData={appData} title="Production" setAppData={setAppData} />}
          </div>

          <DeploymentsSection
            deployments={deployments}
            searchQuery={searchQuery}
            onSearchChange={handleSearchInputChange}
            onOpenRollback={() => setActiveModal(MODAL_TYPES.ROLLBACK)}
            onOpenCheckpoint={() => setActiveModal(MODAL_TYPES.CHECKPOINT)}
            pageTrack={pageTrack}
            pagination={pagination}
            onPageChange={onPageChangeAPI}
            onLimitChange={onLimitChangeAPI}
          />
        </div>
      </div>

      <BuilderModal isOpen={!!activeModal} onClose={() => setActiveModal(null)} modalName={getModalName()}>
        {activeModal === MODAL_TYPES.APP && (
          <AddApplicationModal editData={editData} addApplication={addApplication} editApplication={editApplication} />
        )}
        {activeModal === MODAL_TYPES.IMPORT && <ImportAppModal id={appData.id} onClose={() => setActiveModal(null)} />}
        {activeModal === MODAL_TYPES.ROLLBACK && (
          <RollbackAppVersion
            appId={appData.id}
            onSave={handleSaveRollbackAppVersion}
            onRefresh={() => fetchAppBackup("", 1, 10, params.appId)}
          />
        )}
        {activeModal === MODAL_TYPES.CHECKPOINT && (
          <CheckPointAppVersion
            onSave={(comment) => handleSaveCheckPointAppVersion(comment)}
            onRefresh={() => fetchAppBackup("", 1, 10, params.appId)}
          />
        )}
      </BuilderModal>
    </div>
  );
};

export default AppSettingsPage;
